import { addressToScriptHash, scriptBuilderParamTo, } from '@neo-one/client-common';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { InvocationCallError, NothingToTransferError, NotImplementedError } from '../errors';
import { ACCOUNT_CHANGED, NETWORK_CHANGED, } from './Dapi';
import { UserAccountProviderBase, } from './UserAccountProviderBase';
const paramToArgDataTypeCallbacks = {
    undefined: () => ({
        type: 'ByteArray',
        value: Buffer.alloc(0, 0),
    }),
    array: (param) => ({
        type: 'Array',
        value: param.map(paramToArgDataType),
    }),
    map: (param) => ({
        type: 'Array',
        value: Array.from(param).map(([k, v]) => [paramToArgDataType(k), paramToArgDataType(v)]),
    }),
    uInt160: (param) => ({
        type: 'Hash160',
        value: param,
    }),
    uInt256: (param) => ({
        type: 'Hash256',
        value: param,
    }),
    ecPoint: (param) => ({
        type: 'ByteArray',
        value: param,
    }),
    number: (param) => ({
        type: 'Integer',
        value: param,
    }),
    bn: (param) => ({
        type: 'Integer',
        value: param.toString(10),
    }),
    string: (param) => ({
        type: 'String',
        value: param,
    }),
    boolean: (param) => ({
        type: 'Boolean',
        value: param,
    }),
    buffer: (param) => ({
        type: 'ByteArray',
        value: param,
    }),
    object: (param) => ({
        type: 'Array',
        value: Object.entries(param).map(([k, v]) => [paramToArgDataType(k), paramToArgDataType(v)]),
    }),
};
const paramToArgDataType = (param) => scriptBuilderParamTo(param, paramToArgDataTypeCallbacks);
export class DapiUserAccountProvider extends UserAccountProviderBase {
    constructor({ dapi, provider, onError, }) {
        super({ provider });
        this.dapi = dapi;
        this.mutableInitPromise = this.init();
        this.userAccountsInternal$ = new BehaviorSubject([]);
        this.currentAccountInternal$ = new BehaviorSubject(undefined);
        this.networksInternal$ = new BehaviorSubject([]);
        this.defaultNetworkInternal$ = new BehaviorSubject(undefined);
        const updateAccount = (networkType, dapiAccount) => {
            this.dapi
                .getPublicKey()
                .then(({ publicKey, address }) => {
                if (dapiAccount.address === address) {
                    this.updateUserAccount({
                        network: networkType,
                        publicKey,
                        ...dapiAccount,
                    });
                }
            })
                .catch((err) => {
                if (onError) {
                    onError(err);
                }
            });
        };
        let network;
        let account;
        this.dapi.addEventListener(ACCOUNT_CHANGED, (dapiAccount) => {
            account = dapiAccount;
            if (network !== undefined) {
                updateAccount(network, dapiAccount);
            }
        });
        this.dapi.addEventListener(NETWORK_CHANGED, (dapiNetworks) => {
            network = dapiNetworks.defaultNetwork;
            this.updateNetworks(dapiNetworks);
            if (account !== undefined) {
                updateAccount(dapiNetworks.defaultNetwork, account);
            }
        });
        this.userAccounts$ = this.userAccountsInternal$;
        this.currentUserAccount$ = this.currentAccountInternal$.pipe(distinctUntilChanged());
        this.networks$ = this.networksInternal$;
    }
    getCurrentUserAccount() {
        return this.currentAccountInternal$.getValue();
    }
    getUserAccounts() {
        return this.userAccountsInternal$.getValue();
    }
    getNetworks() {
        return this.networksInternal$.getValue();
    }
    async executeTransfer(transfers, from, _attributes, networkFee) {
        await this.initCheck();
        if (transfers.length === 0) {
            throw new NothingToTransferError();
        }
        if (transfers.length > 1) {
            throw new NotImplementedError('Multiple transfers per transaction');
        }
        const { amount, asset, to } = transfers[0];
        const { txid } = await this.dapi.send({
            fromAddress: from.address,
            toAddress: to,
            asset,
            amount: amount.toString(),
            fee: networkFee.toString(),
            network: from.network,
        });
        const transaction = await this.provider.getTransaction(from.network, txid);
        if (transaction.type !== 'ContractTransaction') {
            throw new Error('For TS');
        }
        return {
            transaction,
            confirmed: async () => this.provider.getTransactionReceipt(from.network, txid),
        };
    }
    async executeClaim(_from, _attributes, _networkFee) {
        throw new NotImplementedError('executeClaim');
    }
    async executeInvokeScript(_options) {
        throw new NotImplementedError('executeInvokeScript');
    }
    async executeInvokeClaim(_options) {
        throw new NotImplementedError('executeInvokeClaim');
    }
    async executeInvokeMethod({ invokeMethodOptions, from, attributes, inputs, outputs, rawInputs, rawOutputs, verify, reorderOutputs, onConfirm, }) {
        await this.initCheck();
        const { contract, invokeMethod, params } = invokeMethodOptions;
        const { txid } = await this.dapi.invoke({
            scriptHash: addressToScriptHash(contract),
            operation: invokeMethod,
            args: params.map(paramToArgDataType),
            network: this.defaultNetworkInternal$.getValue(),
            triggerContractVerification: verify,
            txHashAttributes: this.convertDapiAttributes(attributes),
            assetIntentOverrides: {
                inputs: this.convertDapiInputs(rawInputs.concat(inputs)),
                outputs: this.convertDapiOutputs(reorderOutputs(rawOutputs.concat(outputs))),
            },
        });
        const transaction = await this.provider.getTransaction(from.network, txid);
        if (transaction.type !== 'InvocationTransaction') {
            throw new InvocationCallError(`Expected InvocationTransaction, received ${transaction.type}`);
        }
        return {
            transaction,
            confirmed: async () => {
                const [receipt, data] = await Promise.all([
                    this.provider.getTransactionReceipt(from.network, txid),
                    this.provider.getInvocationData(from.network, txid),
                ]);
                return onConfirm({ transaction, receipt, data });
            },
        };
    }
    convertDapiAttributes(attributes) {
        return attributes.map((attribute) => {
            const argBase = paramToArgDataType(attribute.data);
            return {
                ...argBase,
                txAttrUsage: attribute.usage,
            };
        });
    }
    async init() {
        await new Promise(async (resolve, reject) => {
            this.dapi.addEventListener('READY', resolve);
            setTimeout(() => reject(new Error(`Timed out waiting for Provider to emit 'READY'.`)), 10000);
        });
        const [{ networks, defaultNetwork }, { publicKey }, { address, label }] = await Promise.all([
            this.dapi.getNetworks(),
            this.dapi.getPublicKey(),
            this.dapi.getAccount(),
        ]);
        this.updateNetworks({ networks, defaultNetwork });
        this.updateUserAccount({ network: defaultNetwork, publicKey, address, label });
    }
    async initCheck() {
        try {
            await this.mutableInitPromise;
        }
        catch (error) {
            this.mutableInitPromise = this.init();
            throw error;
        }
    }
    updateUserAccount({ network, publicKey, address, label, }) {
        const userAccount = {
            id: {
                address,
                network,
            },
            name: label === undefined ? address : label,
            publicKey,
        };
        this.currentAccountInternal$.next(userAccount);
        this.userAccountsInternal$.next([userAccount]);
    }
    updateNetworks({ networks, defaultNetwork }) {
        this.networksInternal$.next(networks);
        this.defaultNetworkInternal$.next(defaultNetwork);
    }
    convertDapiInputs(inputs) {
        return inputs.map((input) => ({
            txid: input.hash,
            index: input.index,
        }));
    }
    convertDapiOutputs(outputs) {
        return outputs.map((output) => ({
            asset: output.asset,
            address: output.address,
            value: output.value.toString(),
        }));
    }
}

//# sourceMappingURL=DapiUserAccountProvider.js.map
